import { ReactNode, useEffect, useRef, useState } from 'react';

// Small presentational primitives for the player profiles. Deliberately plain divs and inline
// widths — the project has no charting dependency and should not gain one for six bar charts.

export interface Segment {
  key: string;
  label: string;
  value: number;
  color: string;
  /** Shown under the legend entry. */
  note?: string;
}

export function Stat({ label, value, sub, hint }: {
  label: string; value: ReactNode; sub?: ReactNode; hint?: string;
}) {
  return (
    <div className="prof-stat" title={hint}>
      <div className="prof-stat-value">{value}</div>
      <div className="prof-stat-label">{label}</div>
      {sub !== undefined && <div className="prof-stat-sub">{sub}</div>}
    </div>
  );
}

/** One horizontal bar split by category, with a legend that carries the actual counts. */
export function StackBar({ segments, total }: { segments: Segment[]; total?: number }) {
  const sum = total ?? segments.reduce((a, s) => a + s.value, 0);
  const shown = segments.filter(s => s.value > 0);
  if (!sum) return <div className="prof-empty">No moves recorded yet.</div>;
  return (
    <>
      <div className="prof-stack">
        {shown.map(s => (
          <span
            key={s.key}
            className="prof-stack-seg"
            style={{ width: `${(100 * s.value) / sum}%`, background: s.color }}
            title={`${s.label}: ${s.value.toLocaleString()} (${((100 * s.value) / sum).toFixed(1)}%)`}
          />
        ))}
      </div>
      <div className="prof-legend">
        {shown.map(s => (
          <div key={s.key} className="prof-legend-item">
            <span className="rule-dot" style={{ background: s.color }} />
            <span className="prof-legend-label">{s.label}</span>
            <span className="prof-legend-value">{((100 * s.value) / sum).toFixed(1)}%</span>
            <span className="prof-dim">{s.value.toLocaleString()}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * A labeled bar with an optional reference tick — the population value. Every comparison on
 * the profile page is drawn this way so "you" and "everyone" are always on the same scale.
 */
export function CompareRow({ label, color, value, max, reference, right, refTitle }: {
  label: ReactNode;
  color: string;
  value: number;
  max: number;
  /** Population value, drawn as a tick on the same axis. */
  reference?: number | null;
  right?: ReactNode;
  refTitle?: string;
}) {
  const w = max > 0 ? Math.max(0, Math.min(100, (100 * value) / max)) : 0;
  const refW = reference != null && max > 0 ? Math.max(0, Math.min(100, (100 * reference) / max)) : null;
  return (
    <div className="prof-row">
      <span className="prof-row-label">{label}</span>
      <span className="prof-row-track">
        <span className="prof-row-bar" style={{ width: `${w}%`, background: color }} />
        {refW !== null && (
          <span className="prof-row-ref" style={{ left: `${refW}%` }} title={refTitle ?? 'Everyone'} />
        )}
      </span>
      <span className="prof-row-right">{right}</span>
    </div>
  );
}

export interface ShapeItem {
  /** This row's value as a multiple of the population's — above 1 is ahead of everyone. */
  ratio: number;
  /** How much this row counts toward the player's own baseline (samples, solves, share). */
  weight: number;
}

/**
 * Re-express a set of population ratios against the player's *own* average.
 *
 * A player who is uniformly slower than everyone else learns nothing from a card that says so
 * eight times; what they need is the *shape* — which techniques are their strong ones relative to
 * how they play everything else. So the weighted geometric mean of the ratios is divided out, and
 * what is left is each row's departure from the player's own norm.
 *
 * Geometric because these are ratios: 2× slower and 2× faster have to be the same distance from
 * the center, and only logs do that. The returned `span` is the largest departure present, in log
 * units, so every row on a card shares one axis — bounded at both ends: a floor, because a player
 * whose profile is genuinely flat should see a flat card rather than noise stretched to the full
 * width, and a ceiling, because one row off a two-solve sample would otherwise squash every other
 * row on the card to nothing. Past the ceiling a row simply pins to the edge, with its own number
 * beside it saying how far out it really is.
 */
const SPAN_FLOOR = 0.25;              // ±28%
const SPAN_CEILING = Math.log(2.5);   // ±150%

export function relativeShape(items: ShapeItem[]): { rel: number[]; span: number } {
  const logs = items.map(i => Math.log(Math.min(10, Math.max(0.1, i.ratio))));
  const weights = items.map(i => Math.max(0, i.weight));
  const total = weights.reduce((a, b) => a + b, 0);
  const base = total > 0
    ? logs.reduce((a, l, i) => a + l * weights[i], 0) / total
    : logs.reduce((a, b) => a + b, 0) / (logs.length || 1);
  const rel = logs.map(l => Math.exp(l - base));
  const widest = Math.max(SPAN_FLOOR, ...rel.map(r => Math.abs(Math.log(r))));
  return { rel, span: Math.min(SPAN_CEILING, widest) };
}

/**
 * One row of a shape card: a bar growing out of a center line that means "your own average".
 * Right of center is above your norm, left is below. Deliberately green one way and gray the
 * other rather than green/red — the low end of your own profile is not a failing.
 */
export function ShapeRow({ label, rel, span, right, title, onClick, openLabel }: {
  label: ReactNode;
  /** Value relative to the player's own baseline, from `relativeShape`. */
  rel: number;
  /** Half-axis width in log units, shared by every row on the card. */
  span: number;
  right?: ReactNode;
  title?: string;
  /**
   * Makes the whole row the tap target, with a disclosure chevron. A row is 10px of bar and a
   * name — far too small to hit on a phone if only the name were live, which is why this is on
   * the row rather than on the label.
   */
  onClick?: () => void;
  /** What the row opens, for screen readers. */
  openLabel?: string;
}) {
  const t = Math.max(-1, Math.min(1, Math.log(rel) / span));
  const half = 50 * Math.abs(t);
  const above = t >= 0;
  const body = (
    <>
      <span className="prof-row-label">{label}</span>
      <span className="prof-row-track" title={title}>
        <span className="prof-shape-axis" />
        <span
          className="prof-shape-bar"
          style={{
            left: above ? '50%' : `${50 - half}%`,
            width: `${half}%`,
            background: above ? '#4ade80' : '#6f7683',
          }}
        />
      </span>
      {/* Fixed width, not content width: the sample counts differ in length row to row, and a
          content-sized column moved every bar's right edge with them. */}
      <span className="prof-row-right prof-row-right-fixed">{right}</span>
    </>
  );

  if (!onClick) return <div className="prof-row">{body}</div>;
  return (
    <button type="button" className="prof-row prof-row-btn" onClick={onClick} aria-label={openLabel}>
      {body}
      <span className="prof-row-chevron" aria-hidden="true">›</span>
    </button>
  );
}

/** `×1.35`, colored only when it is meaningfully above the player's own average. */
export const relText = (rel: number) => `×${rel.toFixed(2)}`;

/**
 * A card, with the "what am I looking at" note behind a `?`.
 *
 * The note used to be a `title` attribute, which is a desktop-only affordance: a phone has no
 * hover, so tapping the `?` did nothing at all. It is a button toggling a real panel instead —
 * which works the same way with a mouse, so there is only one behavior to reason about.
 */
export function Card({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // `pointerdown` rather than `click`: closing on the press means a tap that lands outside
    // does not also have to be swallowed before it reaches whatever it was aimed at.
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <section className="prof-card" ref={ref}>
      <h3 className="prof-card-title">
        {title}
        {hint && (
          <button
            type="button"
            className={open ? 'prof-info prof-info-on' : 'prof-info'}
            aria-expanded={open}
            aria-label={open ? `Hide what "${title}" means` : `What does "${title}" mean?`}
            onClick={() => setOpen(o => !o)}
          >
            ?
          </button>
        )}
      </h3>
      {hint && open && <p className="prof-info-pop">{hint}</p>}
      {children}
    </section>
  );
}

/** Backfill progress — attribution runs lazily in the background the first time anyone looks. */
export function ProgressNote({ done, total, running }: { done: number; total: number; running: boolean }) {
  if (!running && done >= total) return null;
  const pct = total ? (100 * done) / total : 0;
  return (
    <div className="prof-progress">
      <div className="prof-progress-bar"><span style={{ width: `${pct}%` }} /></div>
      <span className="prof-dim">
        Analyzing replays — {done.toLocaleString()} of {total.toLocaleString()} done.
        These numbers will keep firming up.
      </span>
    </div>
  );
}

export const pctText = (x: number, digits = 1) => `${(100 * x).toFixed(digits)}%`;
export const secText = (ms: number | null) => (ms === null ? '—' : `${(ms / 1000).toFixed(1)}s`);
